var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Zod from 'zod';
import { objectivePrefix, operationPrefix, } from '../agent/index';
import { stringifyContext } from '../utils/context';
import { chunkSchema, getSubSchema } from '../utils/openapi/chunk-schema';
import { deepenSchema } from '../utils/openapi/deepen-schema';
import { mergeSchema } from '../utils/openapi/merge-schema';
import { t } from '../utils/template';
export default class ExternalResourceEvaluator {
    constructor(parser) {
        this.parser = parser;
        this.logger = parser.logger;
    }
    isFeasible(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const message = t([
                objectivePrefix(params),
                'I want to check feasibility of the following external resource to achieve the objective:',
                operationPrefix(params, true),
                '{{#if bodySchema}}body schema: {{bodySchema}}{{/if}}',
                '{{#if querySchema}}query params schema: {{querySchema}}{{/if}}',
                '{{#if pathSchema}}path params schema: {{pathSchema}}{{/if}}',
                'Did I pick an inappropriate external resource for the job?',
                'The schemas provided are partial. The description provided will not convey full meaning.',
                'This is preliminary feasibility check, you will have access to more data later - keep it loose',
            ], {
                bodySchema: JSON.stringify(params.requiredInputSchema.body),
                querySchema: JSON.stringify(params.requiredInputSchema.query),
                pathSchema: JSON.stringify(params.requiredInputSchema.path),
                context: stringifyContext(params.context),
            });
            const { is_this_the_right_external_resource, reason } = yield this.parser.llm.generateStructured({
                messages: [
                    {
                        content: message,
                        role: 'user',
                    },
                ],
                generatorName: 'did_the_user_pick_inappropriate_external_resource',
                generatorDescription: 'Did the user pick the inappropriate external resource?',
                generatorOutputSchema: Zod.object({
                    is_this_the_right_external_resource: Zod.boolean().describe('true if the chosen resource is inappropriate, false if it is appropriate'),
                    reason: Zod.string()
                        .describe('reason why the resource is not correct, need to be a valid reason')
                        .min(10),
                }),
            });
            return [!is_this_the_right_external_resource, reason];
        });
    }
    filterInputSchemas(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return {
                body: yield this.filterInputSchema(Object.assign(Object.assign({}, params), { requiredInputSchema: params.requiredInputSchema.body, inputSchema: params.inputSchema.body })),
                query: yield this.filterInputSchema(Object.assign(Object.assign({}, params), { requiredInputSchema: params.requiredInputSchema.query, inputSchema: params.inputSchema.query })),
                path: yield this.filterInputSchema(Object.assign(Object.assign({}, params), { requiredInputSchema: params.requiredInputSchema.path, inputSchema: params.inputSchema.path })),
            };
        });
    }
    filterInputSchema(params) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            let filteredSchema = (_a = params.requiredInputSchema) !== null && _a !== void 0 ? _a : {};
            const chunks = chunkSchema((_b = params.inputSchema) !== null && _b !== void 0 ? _b : {});
            for (const { schema: chunkSchema, propertyNames } of chunks) {
                const { shortlist } = yield this.parser.llm.generateStructured({
                    messages: [
                        {
                            role: 'user',
                            content: t([
                                objectivePrefix(params, false),
                                operationPrefix(params),
                                'And I came up with this input to the resource:',
                                '```{{filteredSchema}}```',
                                'Your task is to shortlist properties that may be relevant to achieve the objective.',
                                'You must choose from the following JSONSchema:',
                                '```{{chunkSchema}}```',
                            ], {
                                filteredSchema: JSON.stringify(filteredSchema),
                                chunkSchema: JSON.stringify(chunkSchema),
                            }),
                        },
                    ],
                    generatorName: 'shortlist_properties',
                    generatorDescription: 'Shortlist properties that are relevant given the objective',
                    generatorOutputSchema: Zod.object({
                        shortlist: Zod.array(Zod.enum(propertyNames)),
                    }),
                });
                const subSchema = getSubSchema(chunkSchema, shortlist);
                filteredSchema = mergeSchema(filteredSchema, subSchema);
            }
            filteredSchema = deepenSchema((_c = params.inputSchema) !== null && _c !== void 0 ? _c : {}, filteredSchema);
            return filteredSchema;
        });
    }
}
